import { createSlice } from '@reduxjs/toolkit'
import { signOut } from 'store/auth'
import { Collection, PollStatus } from 'constants/index'
import { FirestoreDatabase } from 'index'
import { toast } from 'react-toastify'
import { chunk, first } from 'lodash'
import firebase from 'firebase/app'
import { getUpdatedPollsAlongAssociatedData } from 'utils/polling'
import { ALL_DATES_FILTER } from 'store/event'
import moment from 'moment'
import { sortDataArrayByCreatedAt } from 'utils/times'

export const initialState = {
  error: null,
  inProgress: false,
  pollAssociatedData: null,
  polls: [],
  poll: null,
  questions: [],
  question: null,
  answer: null,
  answers: [],
  answeredBy: '',
  selectedSpeaker: null,
  selectedStatus: 'All',
  selectedDate: ALL_DATES_FILTER,
  confFilter: '',
  currentPage: 0,
  pageSize: 10
}

const pollingSlice = createSlice({
  name: 'polling',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setPollAssociatedData: (state, action) => {
      state.pollAssociatedData = action.payload
    },
    setPolls: (state, action) => {
      state.polls = action.payload
    },
    setConferenceFilter: (state, action) => {
      state.confFilter = action.payload
    },
    updatePollTitle: (state, action) => {
      const { pollId, title } = action.payload
      state.polls.forEach((poll) => {
        if (poll.id === pollId) {
          poll.title = title
        }
      })
    },
    resetPoll: (state, action) => {
      const { pollId } = action.payload
      state.polls.forEach((poll) => {
        if (poll.id === pollId) {
          poll.status = PollStatus.CREATED
        }
      })
    },
    filterDeletedPolls: (state, action) => {
      const { pollId } = action.payload
      state.polls = state.polls.filter((poll) => poll.id !== pollId)
    },
    setPoll: (state, action) => {
      state.poll = action.payload
    },
    setQuestions: (state, action) => {
      state.questions = action.payload
    },
    setQuestion: (state, action) => {
      state.question = action.payload
    },
    setAnsweredBy: (state, action) => {
      state.answeredBy = action.payload
    },
    setAnswers: (state, action) => {
      state.answers = action.payload
    },
    setAnswer: (state, action) => {
      state.answer = action.payload
    },
    setSelectedSpeaker: (state, action) => {
      state.selectedSpeaker = action.payload
    },
    setSelectedStatus: (state, action) => {
      state.selectedStatus = action.payload
    },
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.polls = []
      state.poll = null
      state.questions = []
      state.question = null
      state.answer = null
      state.answers = []
      state.currentPage = 0
      state.pageSize = 10
      state.answeredBy = ''
      state.confFilter = ''
      state.selectedSpeaker = null
      state.pollAssociatedData = null
      state.selectedStatus = 'All'
      state.selectedDate = ALL_DATES_FILTER
    }
  }
})

export const getSpeakerPolls = (conferenceId, speakerId, callback) => {
  return FirestoreDatabase.collection(Collection.POLLS)
    .where('conferenceId', '==', conferenceId)
    .where('speakerId', '==', speakerId)
    .onSnapshot((documents) => {
      const polls = []
      documents.forEach((doc) => {
        polls.push({
          id: doc.id,
          ...doc.data()
        })
      })

      callback(sortDataArrayByCreatedAt(polls).map((item, i) => ({ ...item, srNo: i + 1 })))
    })
}

export const getSpeakerPollsAlongAssociatedData = async (conferenceId, speakerId) => {
  const speakerSnap = await FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).get()
  const speaker = speakerSnap.exists ? { id: speakerSnap.id, ...speakerSnap?.data() } : null

  const pollsData = await FirestoreDatabase.collection(Collection.POLLS)
    .where('conferenceId', '==', conferenceId)
    .where('speakerId', '==', speakerId)
    .get()

  const polls = pollsData.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
  const talkIds = polls?.filter((poll) => poll?.talkId).map((poll) => poll?.talkId)
  const sessionIds = polls
    ?.filter((poll) => !poll?.talkId && poll?.sessionId)
    .map((poll) => poll?.sessionId)
  const eventIds = polls
    ?.filter((poll) => !poll?.talkId && !poll?.sessionId && poll?.eventId)
    .map((poll) => poll?.eventId)

  const promises = [
    getMultipleDocsByIdFromCollection(talkIds, Collection.TALKS),
    getMultipleDocsByIdFromCollection(eventIds, Collection.EVENTS),
    getMultipleDocsByIdFromCollection(sessionIds, Collection.SESSIONS)
  ]

  const [talks, events, sessions] = await Promise.all(promises)

  return getUpdatedPollsAlongAssociatedData(polls, events, sessions, talks, [], speaker?.fullName)
}

export const getConferenceBasedPolls = async (conferenceId) => {
  if (conferenceId) {
    const pollsData = await FirestoreDatabase.collection(Collection.POLLS)
      .where('conferenceId', '==', conferenceId)
      .get()

    const polls = pollsData?.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))
    const speakerIds = polls?.map((poll) => poll.speakerId).filter((id) => id !== undefined)
    const uniqueSpeakerIds = speakerIds?.length ? [...new Set(speakerIds)] : []
    const talkIds = polls?.filter((poll) => poll.talkId).map((poll) => poll?.talkId)
    const sessionIds = polls
      ?.filter((poll) => !poll?.talkId && poll?.sessionId)
      .map((poll) => poll?.sessionId)
    const eventIds = polls
      ?.filter((poll) => !poll?.talkId && !poll?.sessionId && poll?.eventId)
      .map((poll) => poll?.eventId)

    const promises = [
      getMultipleDocsByIdFromCollection(uniqueSpeakerIds, Collection.SPEAKERS),
      getMultipleDocsByIdFromCollection(talkIds, Collection.TALKS),
      getMultipleDocsByIdFromCollection(eventIds, Collection.EVENTS),
      getMultipleDocsByIdFromCollection(sessionIds, Collection.SESSIONS)
    ]

    const [speakers, talks, events, sessions] = await Promise.all(promises)

    const updatedPolls = getUpdatedPollsAlongAssociatedData(
      polls,
      events,
      sessions,
      talks,
      speakers,
      null
    )
    return sortDataArrayByCreatedAt(updatedPolls).map((item, i) => ({ ...item, srNo: i + 1 }))
  }
}

export const getMultipleDocsByIdFromCollection = async (idsList, collectionName) => {
  if (!idsList?.length) {
    return []
  } else {
    const ids = idsList.filter((id) => id && typeof id === 'string')
    const docsData = []
    const batchSize = 10
    const bachedIds = chunk(ids, batchSize)

    for (const batchIds of bachedIds) {
      const snapshot = await FirestoreDatabase.collection(collectionName)
        .where(firebase.firestore.FieldPath.documentId(), 'in', batchIds)
        .get()
      snapshot.docs.forEach((doc) => {
        docsData.push({ id: doc?.id, ...doc.data() })
      })
    }
    return docsData
  }
}

export const getMultipleSpeakerPolls = (conferenceId, speakerIdsList) => async (dispatch) => {
  try {
    dispatch(pollingSlice.actions.setProgress(true))
    const batchSize = 10
    const bachedIds = chunk(speakerIdsList, batchSize)
    const polls = []

    for (const batchIds of bachedIds) {
      const snapshot = await FirestoreDatabase.collection(Collection.POLLS)
        .where('conferenceId', '==', conferenceId)
        .where('speakerId', 'in', batchIds)
        .get()
      snapshot.docs.forEach((doc) => {
        polls.push({ id: doc?.id, ...doc.data() })
      })
    }
    dispatch(pollingSlice.actions.setPolls(polls))
    dispatch(pollingSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(pollingSlice.actions.setError(err))
  }
}

export const getPollAssociatedDataAlongSpeaker =
  (talkId, sessionId, eventId, speakerId) => async (dispatch) => {
    try {
      const speakerPromise = FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).get()
      const promises = [speakerPromise]
      if (talkId) {
        promises.push(FirestoreDatabase.collection(Collection.TALKS).doc(talkId).get())
      } else if (sessionId) {
        promises.push(FirestoreDatabase.collection(Collection.SESSIONS).doc(sessionId).get())
      } else if (eventId) {
        promises.push(FirestoreDatabase.collection(Collection.EVENTS).doc(eventId).get())
      }
      const [speakerSnap, dataSnap] = await Promise.all(promises)
      const data = dataSnap ? { id: dataSnap.id, ...dataSnap.data() } : null
      const speaker = speakerSnap ? { id: speakerSnap.id, ...speakerSnap.data() } : null

      const updatedData = {
        ...data,
        presenter: speaker?.fullName,
        type: ''
      }

      if (talkId) {
        updatedData.type = 'Talk'
      } else if (sessionId) {
        updatedData.type = 'Session'
      } else if (eventId) {
        updatedData.type = 'Event'
      }
      dispatch(pollingSlice.actions.setPollAssociatedData(updatedData))
    } catch (err) {
      dispatch(pollingSlice.actions.setError(err))
    }
  }

export const pollAttachOrDetach = async (pollId, payload) => {
  try {
    const docRef = FirestoreDatabase.collection(Collection.POLLS).doc(pollId)
    await docRef.update(payload)
  } catch (err) {
    // do nothing
  }
}

export const getSubjectPolls = (conferenceId, subjectId, callback) => {
  return FirestoreDatabase.collection(Collection.POLLS)
    .where('conferenceId', '==', conferenceId)
    .where('subjectId', '==', subjectId)
    .onSnapshot((documents) => {
      const polls = []
      documents.forEach((doc) => {
        polls.push({
          id: doc.id,
          ...doc.data()
        })
      })
      callback(sortDataArrayByCreatedAt(polls).map((item, i) => ({ ...item, srNo: i + 1 })))
    })
}

export const getPollById = (pollId, callback) => {
  return FirestoreDatabase.collection(Collection.POLLS)
    .doc(pollId)
    .onSnapshot((doc) => {
      const poll = doc.exists ? { id: doc.id, ...doc.data() } : null
      callback(poll)
    })
}
export const fetchPollById = async (pollId) => {
  try {
    const doc = await FirestoreDatabase.collection(Collection.POLLS).doc(pollId).get()
    return doc.exists ? { id: doc.id, ...doc.data() } : null
  } catch (err) {
    // do nothing
  }
}

export const fetchPollAlongSpeaker = async (pollId) => {
  const doc = await FirestoreDatabase.collection(Collection.POLLS).doc(pollId).get()
  const poll = doc.exists ? { id: doc.id, ...doc.data() } : null
  const speakerDoc =
    poll?.speakerId &&
    (await FirestoreDatabase.collection(Collection.SPEAKERS).doc(poll?.speakerId).get())
  const speaker = { id: speakerDoc.id, ...speakerDoc.data() }

  return { ...poll, presenter: speaker?.fullName }
}

export const alreadyUsedPoll = async (pollId) => {
  try {
    const docSnap = await FirestoreDatabase.collection(Collection.POLLS).doc(pollId).get()
    const poll = { id: docSnap?.id, ...docSnap.data() }
    return poll?.alreadyUsed
  } catch (err) {
    // do nothing
  }
}

export const resetPollById = (pollId) => async (dispatch) => {
  try {
    await FirestoreDatabase.collection(Collection.POLLS).doc(pollId).update({
      status: PollStatus.CREATED
    })

    const batch = FirestoreDatabase.batch()
    const querySnapshot = await FirestoreDatabase.collection(Collection.ANSWERS)
      .where('pollId', '==', pollId)
      .get()
    querySnapshot.forEach((doc) => {
      batch.delete(doc.ref)
    })
    await batch.commit()
    dispatch(pollingSlice.actions.resetPoll({ pollId }))
  } catch (err) {
    // do nothing
  }
}

export const deletePollById =
  (pollId, { validateChecks }) =>
  async (dispatch) => {
    try {
      dispatch(pollingSlice.actions.setProgress(true))
      const pollDocRef = await FirestoreDatabase.collection(Collection.POLLS).doc(pollId).get()
      if (pollDocRef.data()?.alreadyUsed && validateChecks) {
        toast.error('This poll is linked to an event, session or talk and cannot be deleted.')
        dispatch(pollingSlice.actions.setProgress(false))
        return false
      }

      const subCollectionRef = FirestoreDatabase.collection(Collection.POLLS)
        .doc(pollId)
        .collection(Collection.QUESTIONS)

      const querySnapshot = await subCollectionRef.get()

      const batchSize = 500 // firebase allows us to commit a batch max 500 documents
      const batchedQuestionsData = chunk(querySnapshot, batchSize)
      const batch = FirestoreDatabase.batch()

      const batchedPromises = batchedQuestionsData.map((questionChunk) => {
        questionChunk.forEach((doc) => {
          batch.delete(doc.ref)
        })
        return batch.commit()
      })

      await Promise.all(batchedPromises)

      if (pollDocRef?.data()?.status !== PollStatus.CREATED) {
        const pollResultsSnapshot = await FirestoreDatabase.collection(Collection.ANSWERS)
          .where('pollId', '==', pollId)
          .get()

        const pollResults = pollResultsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))

        if (pollResults?.length) {
          const batchedResults = chunk(pollResults, batchSize)
          const resultsBatch = FirestoreDatabase.batch()

          const batchedResultsPromises = batchedResults.map((resultsChunk) => {
            resultsChunk.forEach((doc) => {
              const docRef = FirestoreDatabase.collection(Collection.ANSWERS).doc(doc.id)
              resultsBatch.delete(docRef)
            })
            return batch.commit()
          })

          await Promise.all(batchedResultsPromises)
        }
      }

      const docRef = FirestoreDatabase.collection(Collection.POLLS).doc(pollId)
      batch.delete(docRef)

      await batch.commit()
      dispatch(pollingSlice.actions.setProgress(false))
      return true
    } catch (err) {
      toast.error('Something went wrong while deleting the poll. Please try again.')
      dispatch(pollingSlice.actions.setError(err))
      dispatch(pollingSlice.actions.setProgress(false))
      return false
    }
  }

export const addPollAlongWithQuestions = (payload) => async (dispatch) => {
  try {
    dispatch(pollingSlice.actions.setProgress(true))
    const updatedPayload = {
      title: payload?.title,
      conferenceId: payload?.conferenceId,
      isActive: false,
      alreadyUsed: !!payload?.talkId,
      talkId: payload?.talkId ?? null,
      status: PollStatus.CREATED,
      createdAt: moment().utc().valueOf()
    }
    payload?.speakerId
      ? (updatedPayload.speakerId = payload?.speakerId)
      : (updatedPayload.subjectId = payload?.subjectId)
    const poll = await FirestoreDatabase.collection(Collection.POLLS).add(updatedPayload)
    if (poll?.id) {
      const batch = FirestoreDatabase.batch()
      const colRef = FirestoreDatabase.collection(Collection.POLLS)
        .doc(poll?.id)
        .collection(Collection.QUESTIONS)

      payload?.questions.forEach((question, index) => {
        const questionRef = colRef.doc()
        question?.options.map((opt, index) => (opt.order = index + 1))
        batch.set(questionRef, {
          order: index + 1,
          ...question
        })
      })
      await batch.commit()
      dispatch(pollingSlice.actions.setProgress(false))
    }
    return poll?.id
  } catch (err) {
    dispatch(pollingSlice.actions.setError(err))
    dispatch(pollingSlice.actions.setProgress(false))
  }
}

export const updatePollAlongWithQuestions =
  (pollId, payload, missingQuestionIds) => async (dispatch) => {
    try {
      dispatch(pollingSlice.actions.setProgress(true))
      const updatedPayload = {
        title: payload?.title,
        conferenceId: payload?.conferenceId
      }
      payload?.speakerId
        ? (updatedPayload.speakerId = payload?.speakerId)
        : (updatedPayload.subjectId = payload?.subjectId)
      if (pollId) {
        await FirestoreDatabase.collection(Collection.POLLS).doc(pollId).update(updatedPayload)
        const batch = FirestoreDatabase.batch()
        const newlyAddedQuestions = payload?.questions?.filter((question) => !question.id)
        const existedQuestions = payload?.questions?.filter((question) => question.id)

        const colRef = FirestoreDatabase.collection(Collection.POLLS)
          .doc(pollId)
          .collection(Collection.QUESTIONS)
        newlyAddedQuestions.forEach((question, index) => {
          const docRef = colRef.doc()
          question?.options.map((opt, index) => (opt.order = index + 1))
          batch.set(docRef, { order: existedQuestions?.length + index + 1, ...question })
        })

        existedQuestions.forEach((question) => {
          const docRef = FirestoreDatabase.collection(Collection.POLLS)
            .doc(pollId)
            .collection(Collection.QUESTIONS)
            .doc(question.id)
          const options = question?.options?.map((opt, index) => ({ ...opt, order: index + 1 }))
          const updatedQuestion = {
            title: question.title,
            options: options,
            order: question.order
          }
          batch.update(docRef, updatedQuestion)
        })

        missingQuestionIds.forEach((questionId) => {
          const docRef = FirestoreDatabase.collection(Collection.POLLS)
            .doc(pollId)
            .collection(Collection.QUESTIONS)
            .doc(questionId)
          batch.delete(docRef)
        })
        await batch.commit()
        dispatch(pollingSlice.actions.updatePollTitle({ pollId, title: payload.title }))
        dispatch(pollingSlice.actions.setProgress(false))
      }
    } catch (err) {
      dispatch(pollingSlice.actions.setError(err))
      dispatch(pollingSlice.actions.setProgress(false))
    }
  }

export const updatePollById = (pollId, payload) => async (dispatch) => {
  try {
    await FirestoreDatabase.collection(Collection.POLLS).doc(pollId).update(payload)
  } catch (err) {
    dispatch(pollingSlice.actions.setError(err))
  }
}
export const updatePollStatus = (pollId, status) => async (dispatch) => {
  try {
    await FirestoreDatabase.collection(Collection.POLLS).doc(pollId).update({
      status: status
    })
  } catch (err) {
    dispatch(pollingSlice.actions.setError(err))
  }
}

export const makePollActiveOrInActive = (pollId, isActive) => async (dispatch) => {
  try {
    await FirestoreDatabase.collection(Collection.POLLS).doc(pollId).update({
      isActive: isActive
    })
  } catch (err) {
    dispatch(pollingSlice.actions.setError(err))
  }
}

export const getActivePollWithinSession = (eventId, sessionId, callback) => {
  return FirestoreDatabase.collection(Collection.POLLS)
    .where('eventId', '==', eventId)
    .where('sessionId', '==', sessionId)
    .where('isActive', '==', true)
    .onSnapshot((documents) => {
      if (!documents.empty) {
        const pollDoc = first(documents.docs)
        callback({
          id: pollDoc?.id,
          ...pollDoc?.data()
        })
      } else callback(null)
    })
}

export const getActivePollWithinSessionOrSessionTalks = (sessionId, callback) => {
  return FirestoreDatabase.collection(Collection.POLLS)
    .where('sessionId', '==', sessionId)
    .where('isActive', '==', true)
    .onSnapshot((documents) => {
      if (!documents.empty) {
        const pollDoc = first(documents.docs)
        callback({
          id: pollDoc?.id,
          ...pollDoc?.data()
        })
      } else callback(null)
    })
}

export const getActivePollWithinTalkEvent = (eventId, callback) => {
  return FirestoreDatabase.collection(Collection.POLLS)
    .where('eventId', '==', eventId)
    .where('isActive', '==', true)
    .limit(1)
    .onSnapshot((documents) => {
      if (!documents.empty) {
        const pollDoc = first(documents.docs)
        callback({
          id: pollDoc?.id,
          ...pollDoc?.data()
        })
      } else callback(null)
    })
}

export const setPollQuestionActiveOrInActive =
  (pollId, questionId, isActive) => async (dispatch) => {
    try {
      await FirestoreDatabase.collection(Collection.POLLS)
        .doc(pollId)
        .collection(Collection.QUESTIONS)
        .doc(questionId)
        .update({
          isActive: isActive
        })
    } catch (err) {
      dispatch(pollingSlice.actions.setError(err))
    }
  }

export const fetchPollQuestions = async (pollId) => {
  const polls = await FirestoreDatabase.collection(Collection.POLLS)
    .doc(pollId)
    .collection(Collection.QUESTIONS)
    .orderBy('order', 'asc')
    .get()

  return polls.docs?.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    pollId: pollId
  }))
}

export const fetchPollAlongQuestions = async (pollId) => {
  const doc = await FirestoreDatabase.collection(Collection.POLLS).doc(pollId).get()
  const poll = {
    id: doc.id,
    ...doc.data()
  }
  const polls = await FirestoreDatabase.collection(Collection.POLLS)
    .doc(pollId)
    .collection(Collection.QUESTIONS)
    .orderBy('order', 'asc')
    .get()

  const questions = polls.docs?.map((doc) => ({
    id: doc.id,
    ...doc.data()
  }))
  return {
    ...poll,
    questions: questions
  }
}

export const answerAQuestion = (answerId, answer) => async (dispatch) => {
  try {
    if (!answerId) {
      await FirestoreDatabase.collection(Collection.ANSWERS).add(answer)
    } else {
      await FirestoreDatabase.collection(Collection.ANSWERS)
        .doc(answerId)
        .update({ ...answer })
    }
  } catch (err) {
    dispatch(pollingSlice.actions.setError(err))
  }
}

export const AssociateSubjectPollsToSpeaker = (subjectId, speakerId) => async (dispatch) => {
  try {
    const snapshot = await FirestoreDatabase.collection(Collection.POLLS)
      .where('subjectId', '==', subjectId)
      .get()

    const batch = FirestoreDatabase.batch()
    snapshot.docs.forEach((doc) => {
      const docRef = FirestoreDatabase.collection(Collection.POLLS).doc(doc?.id)
      batch.update(docRef, {
        speakerId: speakerId
      })
    })
    await batch.commit()
  } catch (err) {
    dispatch(pollingSlice.actions.setError(err))
  }
}

export const getQuestionsByPollId = (pollId, callback) => {
  return FirestoreDatabase.collection(Collection.POLLS)
    .doc(pollId)
    .collection(Collection.QUESTIONS)
    .orderBy('order', 'asc')
    .onSnapshot((documents) => {
      const questions = []
      documents.forEach((doc) => {
        questions.push({
          id: doc.id,
          ...doc.data(),
          pollId: pollId
        })
      })
      callback(questions)
    })
}

export const getActiveQuestionWithinPoll = (pollId, callback) => {
  return FirestoreDatabase.collection(Collection.POLLS)
    .doc(pollId)
    .collection(Collection.QUESTIONS)
    .where('isActive', '==', true)
    .onSnapshot((documents) => {
      if (!documents.empty) {
        const doc = first(documents?.docs)
        const question = {
          id: doc?.id,
          ...doc?.data(),
          pollId: pollId
        }
        callback(question)
      }
    })
}

export const getAnswersOfAttendeeForPoll = (pollId, answeredBy, callback) => {
  return FirestoreDatabase.collection(Collection.ANSWERS)
    .where('pollId', '==', pollId)
    .where('answeredBy', '==', answeredBy)
    .onSnapshot((documents) => {
      const answers = documents.docs.map((doc) => {
        return {
          id: doc.id,
          ...doc.data()
        }
      })
      callback(answers)
    })
}

export const getAnswersOfSpecificPoll = (pollId, callback) => {
  return FirestoreDatabase.collection(Collection.ANSWERS)
    .where('pollId', '==', pollId)
    .onSnapshot((documents) => {
      const groupedAnswers = []
      documents.forEach((doc) => {
        const existingGroup = groupedAnswers?.find(
          (group) => group.questionId === doc.data().questionId
        )
        if (existingGroup) {
          existingGroup.answers.push({ id: doc.id, ...doc.data(), pollId: pollId })
        } else {
          groupedAnswers.push({
            questionId: doc.data().questionId,
            answers: [{ id: doc.id, ...doc.data(), pollId: pollId }]
          })
        }
      })
      callback(groupedAnswers)
    })
}

export const fetchAssociatedPolls = async (pollIdsList) => {
  const polls = await getMultipleDocsByIdFromCollection(pollIdsList, Collection.POLLS)
  const speakerIds = polls?.map((poll) => poll?.speakerId)
  const speakers = await getMultipleDocsByIdFromCollection(speakerIds, Collection.SPEAKERS)
  const speakersMap = new Map()
  speakers.forEach((obj) => {
    speakersMap.set(obj.id, obj?.fullName)
  })

  polls.forEach((poll) => {
    poll.presenter = speakersMap.get(poll?.speakerId)
  })
  return polls
}

export const getPollAnswers = async (pollId) => {
  try {
    const response = await FirestoreDatabase.collection(Collection.ANSWERS)
      .where('pollId', '==', pollId)
      .get()

    return response.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (err) {
    // do nothing
  }
}

export default pollingSlice
